import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Save, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { getWorkOrder, getWindowCareTemplate, saveWindowCareTemplate } from "@/lib/api";

const FIELDS = [
  { key: "standard_ext", label: "Standard Panes" },
  { key: "large", label: "Large Panes" },
  { key: "second_story", label: "2nd story panes" },
  { key: "interior", label: "Interior Panes" },
];

// Shown at close-out when the WO carries Window Care lines: the tech confirms
// what was actually on the building so the next quote starts from the truth.
// Saves straight to the site's pane-count template (source = "field").
export const WindowCareCountCheck = ({ woId }) => {
  const [state, setState] = useState(null); // {siteId, quoted, counts, both_sides, saved}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: wo } = await getWorkOrder(woId);
        const wcLines = (wo.line_items || []).filter(li => FIELDS.some(f => f.key === li.wc_key));
        if (!wcLines.length || !wo.site_id) return;
        const quoted = {};
        FIELDS.forEach(f => {
          const li = wcLines.find(x => x.wc_key === f.key);
          const visits = parseInt(li?.wc_visits) || 1;
          quoted[f.key] = li ? Math.round((parseFloat(li.quantity) || 0) / visits) : 0;
        });
        let counts = { ...quoted };
        let bothSides = false;
        let saved = null;
        try {
          const { data } = await getWindowCareTemplate(wo.site_id);
          if (data?.template?.counts) {
            saved = data.template;
            counts = { ...counts, ...Object.fromEntries(FIELDS.map(f => [f.key, data.template.counts[f.key] ?? quoted[f.key]])) };
            bothSides = !!data.template.counts.both_sides;
          }
        } catch { /* no template yet */ }
        if (alive) setState({ siteId: wo.site_id, quoted, counts, both_sides: bothSides, saved });
      } catch { /* WO unreadable — stay hidden */ }
    })();
    return () => { alive = false; };
  }, [woId]);

  if (!state) return null;

  const set = (key, value) => setState(s => ({ ...s, counts: { ...s.counts, [key]: value } }));
  const changed = FIELDS.some(f => Number(state.counts[f.key] || 0) !== Number(state.quoted[f.key] || 0))
    || (state.saved && FIELDS.some(f => Number(state.counts[f.key] || 0) !== Number(state.saved.counts?.[f.key] || 0)));

  const save = async () => {
    setSaving(true);
    try {
      await saveWindowCareTemplate(state.siteId, {
        counts: { ...state.counts, both_sides: state.both_sides },
        source: "field",
      });
      toast.success("Pane counts updated for this building");
      setState(s => ({ ...s, quoted: { ...s.counts } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the pane counts");
    }
    setSaving(false);
  };

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-2.5 space-y-2" data-testid="wc-count-check">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-sky-600" />
        <span className="text-xs font-semibold text-sky-900">Window Care — actual pane counts</span>
      </div>
      <p className="text-[11px] text-slate-500">
        Correct anything that was off so the next quote for this building is right.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map(f => (
          <div key={f.key}>
            <Label className="text-[10px] text-slate-500">
              {f.label}
              <span className="text-slate-400"> · quoted {state.quoted[f.key]}</span>
            </Label>
            <Input
              type="number" min="0" step="1"
              value={state.counts[f.key] ?? 0}
              onChange={e => set(f.key, e.target.value === "" ? 0 : parseInt(e.target.value) || 0)}
              className="h-8 text-sm bg-white"
              data-testid={`wc-actual-${f.key}`}
            />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox" checked={state.both_sides}
            onChange={e => setState(s => ({ ...s, both_sides: e.target.checked }))}
            className="h-3.5 w-3.5 rounded border-sky-300 text-sky-600 focus:ring-sky-500"
            data-testid="wc-actual-both-sides"
          />
          <span className="text-[11px] text-slate-600">Cleaned inside + outside</span>
        </label>
        <Button type="button" size="sm" variant="outline" disabled={saving || !changed}
          className="h-7 text-[11px] gap-1 border-sky-300 text-sky-700 hover:bg-sky-100"
          onClick={save} data-testid="wc-save-actual-counts">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Save counts
        </Button>
      </div>
    </div>
  );
};

export default WindowCareCountCheck;
